import '../dashboard.css';
import '../../card/card.css';
import '../../submission/submission.css';
import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import CoverDisplay from '../../components/ImagesPageGradeLink';
import CardPageCoverDisplay from '../../components/ImagesPage';
import BannerImage from '../../assets/Space.jpg';
import PsaWhiteLogo from '../../../assets/logos/PSA-White.png';

const TARGET_TAG_NAME = 'PSA Vault';

const toGradeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function DashboardPsaVaultTag() {
  const [key, setKey] = useState('cardWall');
  const [gradeView, setGradeView] = useState('all');
  const [tag, setTag] = useState(null);
  const [cardList, setCardList] = useState([]);
  const [soldCards, setSoldCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [submissionTotals, setSubmissionTotals] = useState({
    sub_precost: 0,
    sub_shipping_to: 0,
    sub_shipping_from: 0,
    sub_upcharge: 0,
    sub_total_cost: 0,
  });

  useEffect(() => {
    let mounted = true;

    const loadPsaVaultCards = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        const tagResponse = await Axios.get('https://api.alientrader.cards/tags');
        const matchingTag = (tagResponse.data || []).find(
          (item) => String(item.tag_name || '').toLowerCase() === TARGET_TAG_NAME.toLowerCase()
        );

        if (!matchingTag) {
          if (mounted) {
            setTag(null);
            setCardList([]);
            setErrorMessage(`The "${TARGET_TAG_NAME}" tag does not exist yet.`);
          }
          return;
        }

        const [cardsResponse, soldResponse] = await Promise.all([
          Axios.get(`https://api.alientrader.cards/tags/${matchingTag.tag_id}/cards`),
          Axios.get('https://api.alientrader.cards/data/dbsearch', {
            params: {
              soldStatus: 'sold',
            },
          }),
        ]);

        if (mounted) {
          setTag(cardsResponse.data?.tag || matchingTag);
          setCardList(cardsResponse.data?.cards || []);
          setSoldCards(
            (soldResponse.data || []).filter(
              (card) => String(card.card_sold_at || '').trim() === 'eBay - PSA'
            )
          );
        }
      } catch (error) {
        console.error('Error fetching PSA Vault tag wall:', error);
        if (mounted) {
          setTag(null);
          setCardList([]);
          setSoldCards([]);
          setErrorMessage('Unable to load PSA Vault cards right now.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadPsaVaultCards();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadSubmissionTotals = async () => {
      const submissionIds = [...new Set(
        cardList
          .map((card) => card.card_grade_sub)
          .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
      )];

      if (submissionIds.length === 0) {
        if (mounted) {
          setSubmissionTotals({
            sub_precost: 0,
            sub_shipping_to: 0,
            sub_shipping_from: 0,
            sub_upcharge: 0,
            sub_total_cost: 0,
          });
        }
        return;
      }

      try {
        const responses = await Promise.all(
          submissionIds.map((submissionId) =>
            Axios.get(`https://api.alientrader.cards/submission/${submissionId}`)
          )
        );

        const totals = responses.reduce((acc, response) => {
          const submission = response.data?.[0] || {};
          acc.sub_precost += Number(submission.sub_precost || 0);
          acc.sub_shipping_to += Number(submission.sub_shipping_to || 0);
          acc.sub_shipping_from += Number(submission.sub_shipping_from || 0);
          acc.sub_upcharge += Number(submission.sub_upcharge || 0);
          acc.sub_total_cost += Number(submission.sub_total_cost || 0);
          return acc;
        }, {
          sub_precost: 0,
          sub_shipping_to: 0,
          sub_shipping_from: 0,
          sub_upcharge: 0,
          sub_total_cost: 0,
        });

        if (mounted) {
          setSubmissionTotals(totals);
        }
      } catch (error) {
        console.error('Error fetching PSA Vault submission totals:', error);
        if (mounted) {
          setSubmissionTotals({
            sub_precost: 0,
            sub_shipping_to: 0,
            sub_shipping_from: 0,
            sub_upcharge: 0,
            sub_total_cost: 0,
          });
        }
      }
    };

    loadSubmissionTotals();

    return () => {
      mounted = false;
    };
  }, [cardList]);

  const totalCardCosts = cardList.reduce((sum, card) => sum + Number(card.card_total_cost || 0), 0);
  const soldCount = soldCards.length;
  const soldPriceTotal = soldCards.reduce((sum, card) => sum + Number(card.card_sold_price || 0), 0);
  const soldFeesTotal = soldCards.reduce((sum, card) => sum + Number(card.card_sold_fees || 0), 0);
  const soldShipTotal = soldCards.reduce((sum, card) => sum + Number(card.card_solds_ship || 0), 0);
  const soldProfitTotal = soldCards.reduce((sum, card) => sum + Number(card.card_profit || 0), 0);
  const netSalesTotal = soldPriceTotal - soldFeesTotal - soldShipTotal;
  const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;
  const gradeSourceCards = gradeView === 'sold'
    ? soldCards
    : gradeView === 'inVault'
      ? cardList
      : [...cardList, ...soldCards];
  const gradeCount10 = gradeSourceCards.filter((card) => toGradeNumber(card.card_grade_number) === 10).length;
  const gradeCount95 = gradeSourceCards.filter((card) => toGradeNumber(card.card_grade_number) === 9.5).length;
  const gradeCount9 = gradeSourceCards.filter((card) => toGradeNumber(card.card_grade_number) === 9).length;
  const gradeCount85 = gradeSourceCards.filter((card) => toGradeNumber(card.card_grade_number) === 8.5).length;
  const gradeCount8 = gradeSourceCards.filter((card) => toGradeNumber(card.card_grade_number) === 8).length;
  const gradeCount75 = gradeSourceCards.filter((card) => toGradeNumber(card.card_grade_number) === 7.5).length;
  const gradeCount7 = gradeSourceCards.filter((card) => toGradeNumber(card.card_grade_number) === 7).length;
  const gradeCountBelow7 = gradeSourceCards.filter((card) => {
    const gradeNumber = toGradeNumber(card.card_grade_number);
    return gradeNumber !== null && gradeNumber < 7;
  }).length;

  return (
    <div className="imagesPage">
      <div
        className="subTopBG"
        style={{
          backgroundImage: `url(${BannerImage})`,
          backgroundPosition: 'center -40px',
        }}
      >
        <div className="actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="subBack"></div>
          <div className="sub" style={{ color: '#f4f7fb' }}></div>
          <div className="subEdit" style={{ width: 'auto', marginRight: '0' }}></div>
        </div>
        <div className="subHeader">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <img
              src={PsaWhiteLogo}
              alt="PSA"
              style={{
                opacity: 0.75,
                maxWidth: '180px',
                width: '100%',
                height: 'auto',
              }}
            />
            <div style={{ opacity: 0.9, marginTop: '-20px' }}>Vault</div>
          </div>
        </div>
      </div>

      <div className="subPositionAdjust" style={{ top: '-130px' }}>
        <Container fluid style={{ paddingLeft: '30px', paddingRight: '30px' }}>
          <Row className="d-flex align-items-center justify-content-center" style={{ marginBottom: '10px' }}>
            <Col xs="9" sm="5" lg="5" xl="3" className="custom-info-box shadow-sm">
              <Row><div className="custom-info-header">Information</div></Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Total Card Vaults:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : cardList.length}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Cost:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(submissionTotals.sub_precost)}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Shipping to:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(submissionTotals.sub_shipping_to)}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Shipping from:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(submissionTotals.sub_shipping_from)}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Upcharge Cost:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(submissionTotals.sub_upcharge)}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Total Grading Cost:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(submissionTotals.sub_total_cost)}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Total Card Costs:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(totalCardCosts)}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Link to Vault:</div></Col>
                <Col xs={5} sm={5}>
                  <div className="custom-info-val">
                    <a
                      href="https://app.collectors.com/collection/vaulted"
                      target="_blank"
                      rel="noreferrer"
                    >
                      PSA
                    </a>
                  </div>
                </Col>
              </Row>
              <Row><br /></Row>
            </Col>
          </Row>

          <Row className="d-flex align-items-center justify-content-center" style={{ marginBottom: '10px' }}>
            <Col xs="9" sm="5" lg="3" xl="2" className="custom-info-box shadow-sm">
              <Row><div className="custom-info-header">Grades</div></Row>
              <Row>
                <div
                  className="custom-info-misc"
                  style={{
                    justifyContent: 'center',
                    fontWeight: 700,
                    paddingTop: '6px',
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setGradeView('all')}
                    style={{
                      cursor: 'pointer',
                      textDecoration: gradeView === 'all' ? 'underline' : 'none',
                      border: 'none',
                      background: 'transparent',
                      padding: '0 6px',
                      fontWeight: 700,
                    }}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setGradeView('inVault')}
                    style={{
                      cursor: 'pointer',
                      textDecoration: gradeView === 'inVault' ? 'underline' : 'none',
                      border: 'none',
                      background: 'transparent',
                      padding: '0 6px',
                      fontWeight: 700,
                    }}
                  >
                    In Vault
                  </button>
                  <button
                    type="button"
                    onClick={() => setGradeView('sold')}
                    style={{
                      cursor: 'pointer',
                      textDecoration: gradeView === 'sold' ? 'underline' : 'none',
                      border: 'none',
                      background: 'transparent',
                      padding: '0 6px',
                      fontWeight: 700,
                    }}
                  >
                    Sold
                  </button>
                </div>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">GEM MINT - 10:</div></Col>
                <Col xs={2} sm={2}><div className="custom-info-val">{loading || errorMessage ? '-' : gradeCount10}</div></Col>
                <Col xs={4} sm={4}><div className="custom-info-val"></div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">MT - 9:</div></Col>
                <Col xs={2} sm={2}><div className="custom-info-val">{loading || errorMessage ? '-' : gradeCount9}</div></Col>
                {(!loading && !errorMessage && gradeCount95 > 0) ? (
                  <>
                    <Col xs={1} sm={1}><div className="custom-info-title">9.5+</div></Col>
                    <Col xs={3} sm={3}><div className="custom-info-val">{gradeCount95}</div></Col>
                  </>
                ) : (
                  <>
                    <Col xs={1} sm={1}></Col>
                    <Col xs={3} sm={3}></Col>
                  </>
                )}
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">NM-MT - 8:</div></Col>
                <Col xs={2} sm={2}><div className="custom-info-val">{loading || errorMessage ? '-' : gradeCount8}</div></Col>
                {(!loading && !errorMessage && gradeCount85 > 0) ? (
                  <>
                    <Col xs={1} sm={1}><div className="custom-info-title">8.5+</div></Col>
                    <Col xs={3} sm={3}><div className="custom-info-val">{gradeCount85}</div></Col>
                  </>
                ) : (
                  <>
                    <Col xs={1} sm={1}></Col>
                    <Col xs={3} sm={3}></Col>
                  </>
                )}
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">NM - 7:</div></Col>
                <Col xs={2} sm={2}><div className="custom-info-val">{loading || errorMessage ? '-' : gradeCount7}</div></Col>
                {(!loading && !errorMessage && gradeCount75 > 0) ? (
                  <>
                    <Col xs={1} sm={1}><div className="custom-info-title">7.5+</div></Col>
                    <Col xs={3} sm={3}><div className="custom-info-val">{gradeCount75}</div></Col>
                  </>
                ) : (
                  <>
                    <Col xs={1} sm={1}></Col>
                    <Col xs={3} sm={3}></Col>
                  </>
                )}
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Below 7:</div></Col>
                <Col xs={2} sm={2}><div className="custom-info-val">{loading || errorMessage ? '-' : gradeCountBelow7}</div></Col>
                <Col xs={4} sm={4}><div className="custom-info-val"></div></Col>
              </Row>
              <Row><br /></Row>
            </Col>
          </Row>

          <Row className="d-flex align-items-center justify-content-center" style={{ marginBottom: '10px' }}>
            <Col xs="9" sm="5" lg="3" xl="2" className="custom-info-box shadow-sm">
              <Row><div className="custom-info-header">Sold Info</div></Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title"># of Sold:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : soldCount}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Total Sales:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(soldPriceTotal)}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Net Sales:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(netSalesTotal)}</div></Col>
              </Row>
              <Row className="d-flex align-items-center justify-content-center">
                <Col><div className="custom-info-title">Profit:</div></Col>
                <Col xs={5} sm={5}><div className="custom-info-val">{loading || errorMessage ? '-' : formatCurrency(soldProfitTotal)}</div></Col>
              </Row>
              <Row><br /></Row>
            </Col>
          </Row>

          <Row className="d-flex align-items-center justify-content-center">
            <Col xxl={10} xl={11} lg={12} md={12} className="custom-info-box shadow-sm">
              <Row>
                <Tabs id="psa-vault-card-wall" activeKey={key} onSelect={(nextKey) => setKey(nextKey || 'cardWall')} className="mb-3">
                  <Tab eventKey="cardWall" title="In Vault">
                    <Row className="subCardWall d-flex align-items-center justify-content-center">
                      {loading ? (
                        <Col>
                          <div className="custom-info-misc" style={{ justifyContent: 'center', textAlign: 'center' }}>
                            Loading PSA Vault cards...
                          </div>
                        </Col>
                      ) : errorMessage ? (
                        <Col>
                          <div className="custom-info-misc" style={{ justifyContent: 'center', textAlign: 'center' }}>
                            {errorMessage}
                          </div>
                        </Col>
                      ) : cardList.length > 0 ? (
                        cardList.map((item, index) => (
                          <Col className="imagesPageRow d-flex align-items-center justify-content-center" key={item.card_id}>
                            <CoverDisplay key={index} image={item} />
                          </Col>
                        ))
                      ) : (
                        <Col>
                          <div className="custom-info-misc" style={{ justifyContent: 'center', textAlign: 'center' }}>
                            No cards are currently tagged with "{TARGET_TAG_NAME}".
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Tab>
                  <Tab eventKey="soldWall" title="Sold">
                    <Row className="subCardWall d-flex align-items-center justify-content-center">
                      {loading ? (
                        <Col>
                          <div className="custom-info-misc" style={{ justifyContent: 'center', textAlign: 'center' }}>
                            Loading sold PSA Vault cards...
                          </div>
                        </Col>
                      ) : errorMessage ? (
                        <Col>
                          <div className="custom-info-misc" style={{ justifyContent: 'center', textAlign: 'center' }}>
                            {errorMessage}
                          </div>
                        </Col>
                      ) : soldCards.length > 0 ? (
                        soldCards.map((item, index) => (
                          <Col className="imagesPageRow d-flex align-items-center justify-content-center" key={`${item.card_id}-sold`}>
                            <CardPageCoverDisplay key={index} image={item} />
                          </Col>
                        ))
                      ) : (
                        <Col>
                          <div className="custom-info-misc" style={{ justifyContent: 'center', textAlign: 'center' }}>
                            No PSA Vault cards were sold through eBay - PSA.
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Tab>
                </Tabs>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default DashboardPsaVaultTag;
